import axios from 'axios';

import { API_ASSETS_INFO, API_ASSETS_LIST_URL } from 'constants/api';

import { getIsTestnetEnv } from 'helpers/env';

import { AssetInfo } from 'types/asset-info';
import { AssetsList } from 'types/assets-list';
import { ListResponse } from 'types/vote';

import RequestCache from './request-cache';

const assetsInfoRequests = new RequestCache<AssetInfo | undefined>();
const assetLists = new RequestCache<AssetsList['issuer_orgs'][number]['assets']>(60_000);

type MetadataRequest = {
    asset: string;
    resolve: (info: AssetInfo | undefined) => void;
    reject: (error: unknown) => void;
};
const queues = new Map<string, { requests: MetadataRequest[]; batchSize: number }>();

const enqueueMetadata = (network: string, asset: string, batchSize: number) =>
    new Promise<AssetInfo | undefined>((resolve, reject) => {
        let queue = queues.get(network);
        if (!queue) {
            queue = { requests: [], batchSize };
            queues.set(network, queue);
            const pending = queue;
            queueMicrotask(() => {
                queues.delete(network);
                for (let index = 0; index < pending.requests.length; index += pending.batchSize) {
                    const batch = pending.requests.slice(index, index + pending.batchSize);
                    const params = new URLSearchParams();
                    batch.forEach(request => params.append('asset', request.asset));
                    axios
                        .get<ListResponse<AssetInfo>>(API_ASSETS_INFO, { params })
                        .then(({ data }) => {
                            const metadata = new Map(
                                data.results.map(info => [`${info.code}:${info.issuer}`, info]),
                            );
                            batch.forEach(request => request.resolve(metadata.get(request.asset)));
                        })
                        .catch(error => batch.forEach(request => request.reject(error)));
                }
            });
        }
        queue.batchSize = Math.min(queue.batchSize, batchSize);
        queue.requests.push({ asset, resolve, reject });
    });

export const getAssetsRequest = () =>
    assetLists.get(API_ASSETS_LIST_URL, () =>
        axios.get<AssetsList>(API_ASSETS_LIST_URL).then(({ data }) => {
            const issuerOrgs = data.issuer_orgs;

            return issuerOrgs.reduce((acc, anchor) => {
                anchor.assets.forEach(asset => {
                    if (!asset.disabled && !anchor.disabled && !asset.unlisted) {
                        acc.push(asset);
                    }
                });

                return acc;
            }, []);
        }),
    );

export const getAssetsInfo = async (
    assets: Array<{ code: string; issuer: string }>,
    batchSize: number = 100,
): Promise<AssetInfo[]> => {
    const network = getIsTestnetEnv() ? 'testnet' : 'public';
    const limit = Math.max(1, Math.min(100, Math.floor(batchSize) || 100));
    const keys = [...new Set(assets.map(({ code, issuer }) => `${code}:${issuer}`))];
    const results = await Promise.all(
        keys.map(key =>
            assetsInfoRequests.get(`${network}:${key}`, () => enqueueMetadata(network, key, limit)),
        ),
    );
    return results.filter((info): info is AssetInfo => Boolean(info));
};
